import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ShieldCheck, Scale, SlidersHorizontal, Trash2, LifeBuoy } from "lucide-react";
import { LineReveal, Reveal } from "../components/site/Reveal";

const SILK = "https://images.pexels.com/photos/8426345/pexels-photo-8426345.jpeg";
const SALON = "https://images.unsplash.com/photo-1781450090585-1a511b7066d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtaW5pbWFsJTIwbmV1dHJhbCUyMGJlYXV0eSUyMHNhbG9uJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg2MzAxOTgxfDA&ixlib=rb-4.1.0&q=85";

const cards = [
  { to: "/privacy-policy", label: "Privacy Policy", desc: "What we collect, why, and how it's protected.", index: "01", Icon: ShieldCheck },
  { to: "/terms-of-service", label: "Terms of Service", desc: "The rules of using ProBeauty Connect.", index: "02", Icon: Scale },
  { to: "/privacy-choices", label: "Your Privacy Choices", desc: "Manage your data, request access, opt out.", index: "03", Icon: SlidersHorizontal },
  { to: "/delete-account", label: "Delete Your Account", desc: "Permanently remove your data from ProBeauty.", index: "04", Icon: Trash2 },
];

const Card = ({ to, label, desc, index, Icon, delay }) => (
  <Reveal delay={delay}>
    <Link
      to={to}
      data-testid={`hub-card-${to.replace("/", "")}`}
      className="group relative flex flex-col justify-between h-full min-h-[220px] border border-[#E0E0D9] bg-[#F9F9F6] p-8 transition-colors duration-300 hover:bg-[#F3F3EF]"
    >
      <div className="flex items-start justify-between">
        <Icon size={26} strokeWidth={1.25} className="text-[#0A0A0A] transition-colors duration-300 group-hover:text-[#C6826D]" />
        <span className="font-mono-meta text-[11px] text-[#8A8A8A]">{index}</span>
      </div>
      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-serif-display text-2xl md:text-3xl font-normal tracking-tight">{label}</h3>
          <ArrowUpRight size={20} className="text-[#C6826D] opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
        </div>
        <p className="mt-2 text-sm text-[#525252] leading-relaxed max-w-xs">{desc}</p>
      </div>
    </Link>
  </Reveal>
);

export default function LegalHome() {
  return (
    <div className="relative z-10">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-end">
          <div className="md:col-span-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="font-mono-meta text-[11px] uppercase tracking-[0.3em] text-[#C6826D]"
            >
              ProBeauty Connect · Legal & Privacy
            </motion.div>
            <h1 className="mt-7 font-serif-display font-light tracking-tight leading-[0.92] text-[16vw] sm:text-7xl md:text-8xl lg:text-[6.5rem]">
              <LineReveal lines={["Your data,", "handled with", "care."]} delay={0.15} stagger={0.11} />
            </h1>
          </div>
          <div className="md:col-span-4">
            <Reveal delay={0.6}>
              <p className="text-lg text-[#525252] leading-relaxed">
                A curated marketplace connecting licensed beauty professionals with
                salon owners hiring. Here's exactly what we collect, why, and how
                you can control it.
              </p>
              <Link
                to="/privacy-policy"
                data-testid="hero-cta"
                className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-[#F9F9F6] px-7 py-4 text-sm tracking-tight transition-colors duration-300 hover:bg-[#C6826D]"
              >
                Read the Privacy Policy
                <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>

        {/* hero image band */}
        <Reveal delay={0.35} className="mt-16 md:mt-20">
          <div className="relative border border-[#E0E0D9] overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-7 relative aspect-[16/10] md:aspect-auto md:min-h-[380px] overflow-hidden">
                <motion.img
                  src={SALON}
                  alt="Beauty salon interior"
                  className="absolute inset-0 w-full h-full object-cover grayscale-[0.15]"
                  initial={{ scale: 1.12 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <div className="md:col-span-5 bg-[#F3F3EF] p-8 md:p-12 flex flex-col justify-between border-t md:border-t-0 md:border-l border-[#E0E0D9]">
                <div className="font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A]">
                  Trust, by design
                </div>
                <div className="mt-6 space-y-6">
                  {[
                    ["No ad networks", "We don't track you across other apps or websites."],
                    ["No data brokers", "We never sell your personal information."],
                    ["No analytics SDKs", "No Mixpanel, Amplitude, Segment or Firebase Analytics."],
                  ].map(([t, d]) => (
                    <div key={t}>
                      <div className="font-serif-display text-2xl">{t}</div>
                      <div className="text-sm text-[#525252] mt-1">{d}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* MARQUEE ribbon */}
      <section className="border-y border-[#E0E0D9] bg-[#EBEBE5] py-6 md:py-8 overflow-hidden">
        <Marquee speed={38} gradient={false} autoFill>
          {["Privacy Policy", "Terms of Service", "Your Privacy Choices", "Delete Your Account", "GDPR", "CCPA / CPRA"].map((t, i) => (
            <span key={i} className="marquee-serif text-[#0A0A0A] text-4xl md:text-6xl font-light italic mx-16 md:mx-24 inline-flex items-center gap-16 md:gap-24">
              {t}
              <span className="w-2 h-2 bg-[#C6826D] rotate-45 not-italic" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* HUB CARDS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <div className="md:col-span-4">
            <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A]">
              Documents
            </div>
            <h2 className="mt-5 font-serif-display text-4xl md:text-5xl font-light tracking-tight leading-tight">
              Everything, in plain sight
            </h2>
            <p className="mt-5 text-[#525252] leading-relaxed max-w-sm">
              Four documents. No fine print games. Read what applies to you, or
              jump straight to managing your data.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
            {cards.map((c, i) => (
              <Card key={c.to} {...c} delay={i * 0.08} />
            ))}
          </div>
        </div>

        {/* contact strip */}
        <Reveal className="mt-16 md:mt-20">
          <Link
            to="/support"
            data-testid="hub-contact-support"
            className="group flex flex-col md:flex-row md:items-center justify-between gap-6 border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F9F6] p-8 md:p-10 transition-colors duration-300 hover:bg-[#C6826D] hover:border-[#C6826D]"
          >
            <div className="flex items-center gap-5">
              <LifeBuoy size={28} strokeWidth={1.25} />
              <div>
                <div className="font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#F9F9F6]/60">
                  Need help or have a question?
                </div>
                <div className="font-serif-display text-3xl md:text-4xl mt-1">Contact &amp; Support</div>
              </div>
            </div>
            <ArrowUpRight size={30} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
